/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmployerFilterDropdown from './EmployerFilterDropdown';
import { 
  faUser, 
  faEnvelope, 
  faPhone, 
  faMapMarkerAlt, 
  faBriefcase, 
  faGraduationCap, 
  faCalendar, 
  faStar, 
  faDownload, 
  faEye, 
  faCheck, 
  faTimes, 
  faComments,
  faClock,
  faFilter,
  faSearch,
  faChevronDown,
  faChevronUp,
  faBuilding,
  faDollarSign,
  faCheckCircle,
  faEllipsisV,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import { resolveDownloadFilename } from '../utils/downloadFilename';
import axios from 'axios';
import '../styles/RecruiterCandidateTracker.css';
import ThemedLoadingSpinner from './ThemedLoadingSpinner';

const CACHE_TTL_MS = 2 * 60 * 1000;

let cachedJobApplications = null;
let cachedInternshipApplications = null;
let candidateTrackerCacheTimestamp = 0;
let cachedTokenSignature = null;

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'ai_screening', label: 'AI Screening' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interview', label: 'Interview' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'offered', label: 'Offered' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];

const INTERVIEW_MODE_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-Person' },
  { value: 'phone', label: 'Phone' },
];

const RecruiterCandidateTracker = ({ onViewCandidate, viewUserId, isMobileView = false, useCustomDropdown = false }) => {
  const shouldUseCustomDropdown = isMobileView || useCustomDropdown;
  const [applications, setApplications] = useState([]);
  const [internshipApplications, setInternshipApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewType, setViewType] = useState('jobs'); // 'jobs' or 'internships'
  const [expandedApplication, setExpandedApplication] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewing: 0,
    interview: 0,
    offered: 0,
    hired: 0,
    rejected: 0
  });
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalApplication, setScheduleModalApplication] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    interview_date: '',
    interview_start_time: '',
    interview_end_time: '',
    interview_mode: 'online',
    interview_link: '',
    interview_location: '',
    interview_notes: ''
  });
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionMenu && !event.target.closest('.action-menu-container')) {
        setOpenActionMenu(null);
      }
    };

    if (openActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openActionMenu]);

  const getTokenSignature = () => {
    const token = localStorage.getItem('token');
    return token ? token.slice(-12) : null;
  };

  const clearCache = () => {
    cachedJobApplications = null;
    cachedInternshipApplications = null;
    candidateTrackerCacheTimestamp = 0;
    cachedTokenSignature = null;
  };

  const updateCache = (
    jobsData,
    internshipsData,
    timestamp = Date.now(),
    tokenSignature = cachedTokenSignature ?? getTokenSignature()
  ) => {
    cachedJobApplications = jobsData;
    cachedInternshipApplications = internshipsData;
    candidateTrackerCacheTimestamp = timestamp;
    cachedTokenSignature = tokenSignature;
  };

  const applyCachedDataIfAvailable = () => {
    const hasJobCache = Array.isArray(cachedJobApplications);
    const hasInternshipCache = Array.isArray(cachedInternshipApplications);
    const tokenSignature = getTokenSignature();

    if (
      cachedTokenSignature &&
      tokenSignature &&
      cachedTokenSignature !== tokenSignature
    ) {
      clearCache();
      return { usedCache: false, isStale: true };
    }

    if (!hasJobCache && !hasInternshipCache) {
      return { usedCache: false, isStale: true };
    }

    setApplications(cachedJobApplications || []);
    setInternshipApplications(cachedInternshipApplications || []);
    setError(null);
    setLoading(false);

    const isTimeStale =
      !candidateTrackerCacheTimestamp ||
      Date.now() - candidateTrackerCacheTimestamp > CACHE_TTL_MS;

    // If cache was created before we added applicant location fields, force a refresh
    // so the UI can render location under the applicant name.
    const cacheMissingLocationFields = (() => {
      const sample =
        (Array.isArray(cachedJobApplications) && cachedJobApplications[0]) ||
        (Array.isArray(cachedInternshipApplications) && cachedInternshipApplications[0]) ||
        null;
      if (!sample || typeof sample !== 'object') return false;
      return !Object.prototype.hasOwnProperty.call(sample, 'applicant_location_display');
    })();

    const isStale = isTimeStale || cacheMissingLocationFields;

    return { usedCache: true, isStale };
  };

  useEffect(() => {
    const { usedCache, isStale } = applyCachedDataIfAvailable();

    if (!usedCache) {
      fetchApplications({ showSpinner: true });
    } else if (isStale) {
      fetchApplications({ showSpinner: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUserId]); // Reload when viewUserId changes (for admin view mode)

  useEffect(() => {
    calculateStats();
  }, [applications, internshipApplications, viewType]);

  const fetchApplications = async ({ showSpinner = true } = {}) => {
    try {
      if (showSpinner) {
        setLoading(true);
      }
      setError(null);
      const token = localStorage.getItem('token');
      const tokenSignature = token ? token.slice(-12) : null;
      let jobsData = [];
      let internshipsData = [];
      
      // Build URL with userId parameter if admin view mode
      const jobsUrl = viewUserId 
        ? buildApiUrl(`/api/recruiters/applications?userId=${viewUserId}`)
        : buildApiUrl('/api/recruiters/applications');
      
      // Fetch job applications from the recruiter applications endpoint
      const jobsResponse = await axios.get(
        jobsUrl,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      console.log('📊 Fetched job applications:', jobsResponse.data);
      jobsData = jobsResponse.data || [];
      setApplications(jobsData);
      
      // Fetch internship applications
      try {
        const internshipsUrl = viewUserId
          ? buildApiUrl(`/api/recruiters/internship-applications?userId=${viewUserId}`)
          : buildApiUrl('/api/recruiters/internship-applications');
          
        const internshipsResponse = await axios.get(
          internshipsUrl,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        console.log('📊 Fetched internship applications:', internshipsResponse.data);
        internshipsData = internshipsResponse.data || [];
        setInternshipApplications(internshipsData);
      } catch (internError) {
        console.log('No internship applications endpoint or no data:', internError);
        setInternshipApplications([]);
      }

      updateCache(jobsData, internshipsData, Date.now(), tokenSignature);
      
    } catch (err) {
      console.error('Error fetching applications:', err);
      if (!applications.length && !internshipApplications.length) {
        setError('Failed to load applications. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    // Use current view type to calculate stats
    const currentApps = viewType === 'jobs' ? applications : internshipApplications;
    
    const newStats = {
      total: currentApps.length,
      pending: currentApps.filter(app => app.status === 'pending').length,
      reviewing: currentApps.filter(app => app.status === 'reviewing').length,
      interview: currentApps.filter(app => ['interview', 'interview_scheduled'].includes(app.status)).length,
      offered: currentApps.filter(app => app.status === 'offered').length,
      hired: currentApps.filter(app => app.status === 'hired').length,
      rejected: currentApps.filter(app => app.status === 'rejected').length
    };
    setStats(newStats);
  };

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
      if (value.$date) return value.$date;
      if (value.seconds) return new Date(value.seconds * 1000).toISOString();
      if (value._seconds) return new Date(value._seconds * 1000).toISOString();
    }
    try {
      const direct = new Date(value);
      if (!Number.isNaN(direct.getTime())) {
        return direct.toISOString();
      }
    } catch (err) {
      console.warn('Unable to normalize date value', value, err);
    }
    return null;
  };

  const formatTimezoneLabel = (tz) => {
    if (!tz || typeof tz !== 'string') return 'UTC';
    const cleaned = tz.trim();
    const parts = cleaned.split('/');
    if (parts.length <= 1) {
      return cleaned.replace('_', ' ');
    }
    const city = parts[parts.length - 1].replace(/_/g, ' ');
    return `${city} (${cleaned})`;
  };

  const formatDateTimeInZone = (value, timeZone) => {
    const normalized = normalizeDateValue(value);
    if (!normalized) return 'N/A';
    let resolvedZone = timeZone;
    if (!resolvedZone || typeof resolvedZone !== 'string' || !resolvedZone.trim()) {
      resolvedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: resolvedZone
      }).format(new Date(normalized));
    } catch (error) {
      console.warn('Failed to format date in timezone', { normalized, resolvedZone, error });
      const fallback = new Date(normalized);
      return Number.isNaN(fallback.getTime()) ? 'N/A' : fallback.toLocaleString();
    }
  };

  const formatTimeDifference = (minutes) => {
    if (minutes === null || minutes === undefined) return null;
    if (minutes === 0) return 'Same local time';
    const sign = minutes > 0 ? '+' : '-';
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (mins) parts.push(`${mins}m`);
    if (parts.length === 0) {
      parts.push('0m');
    }
    return `${sign}${parts.join(' ')}`.trim();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return '#f59e0b';
      case 'ai_screening': return '#06b6d4';
      case 'reviewing': return '#3b82f6';
      case 'shortlisted': return '#8b5cf6';
      case 'interview':
      case 'interview_scheduled': return '#8b5cf6';
      case 'offered': return '#10b981';
      case 'hired': return '#059669';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return faClock;
      case 'ai_screening': return faComments;
      case 'reviewing': return faEye;
      case 'shortlisted': return faStar;
      case 'interview':
      case 'interview_scheduled': return faComments;
      case 'offered': return faCheck;
      case 'hired': return faCheckCircle;
      case 'rejected': return faTimes;
      default: return faClock;
    }
  };

  const formatDate = (dateInput) => {
    const normalized = normalizeDateValue(dateInput);
    if (!normalized) return 'N/A';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return 'N/A';
    // Format as "Jan 21, 2026" to match candidate tracker - explicit formatting
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const getApplicationIdentifiers = (application) => {
    const applicantIdRaw =
      application.applicant_id ||
      application.userId ||
      application.applicantId ||
      application.candidate_id ||
      application.candidateId;

    const jobId =
      application.job_id ||
      application.jobId ||
      application.job?.job_id ||
      application.job?.id ||
      application.job?._id ||
      application.jobIdRef;

    const internshipId =
      application.internship_id ||
      application.internshipId ||
      application.internship?.id ||
      application.internship?._id;

    const interviewType = internshipId ? 'internship' : 'job';
    const resolvedJobIdRaw = internshipId || jobId;
    const resolvedJobId = resolvedJobIdRaw ? String(resolvedJobIdRaw) : null;
    const normalizedApplicantId = applicantIdRaw ? String(applicantIdRaw) : null;
    const applicationId = application._id ? String(application._id) : null;

    return {
      applicantId: normalizedApplicantId,
      jobId: resolvedJobId,
      interviewType,
      applicationId
    };
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      interview_date: '',
      interview_start_time: '',
      interview_end_time: '',
      interview_mode: 'online',
      interview_link: '',
      interview_location: '',
      interview_notes: ''
    });
    setScheduleModalApplication(null);
  };

  const getTodayDateKey = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const openScheduleModal = (application) => {
    const identifiers = getApplicationIdentifiers(application);
    if (!identifiers.applicantId || !identifiers.jobId) {
      alert('Missing applicant or job information for scheduling.');
      return;
    }

    // Use existing interview data if rescheduling, otherwise default to today + 30 min
    const presetDate = application.interview_date
      ? application.interview_date.split('T')[0] || application.interview_date
      : getTodayDateKey();
    const presetStart = application.interview_start_time || application.interview_time || '10:00';
    let presetEnd = application.interview_end_time || '';
    if (!presetEnd && presetStart) {
      const [h, m] = presetStart.split(':').map(Number);
      const endMin = (m || 0) + 30;
      const endH = h + Math.floor(endMin / 60);
      const endM = endMin % 60;
      presetEnd = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    }
    if (!presetEnd) presetEnd = '10:30';

    setScheduleModalApplication({
      ...application,
      ...identifiers
    });
    setScheduleForm({
      interview_date: presetDate,
      interview_start_time: presetStart,
      interview_end_time: presetEnd,
      interview_mode: application.interview_mode || 'online',
      interview_link: application.interview_link || '',
      interview_location: application.interview_location || '',
      interview_notes: application.interview_notes || ''
    });
    setScheduleModalOpen(true);
  };

  const closeScheduleModal = () => {
    setScheduleModalOpen(false);
    resetScheduleForm();
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduleModalApplication) {
      return;
    }

    const identifiers = getApplicationIdentifiers(scheduleModalApplication);
    if (!identifiers.applicantId || !identifiers.jobId) {
      alert('Missing applicant or job information for scheduling.');
      return;
    }

    if (!scheduleForm.interview_date || !scheduleForm.interview_start_time) {
      alert('Please provide both the interview date and start time.');
      return;
    }

    if (scheduleForm.interview_mode === 'online' && (!scheduleForm.interview_link || !scheduleForm.interview_link.trim())) {
      alert('Meeting Link is required when Interview Mode is Online. Please enter a meeting link to schedule the interview.');
      return;
    }

    try {
      setScheduleSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(
        buildApiUrl('/api/applications/schedule-interview'),
        {
          applicant_id: identifiers.applicantId,
          job_id: identifiers.jobId,
          application_id: identifiers.applicationId,
          interview_date: scheduleForm.interview_date,
          interview_start_time: scheduleForm.interview_start_time,
          interview_end_time: scheduleForm.interview_end_time,
          interview_mode: scheduleForm.interview_mode,
          interview_link: scheduleForm.interview_link,
          interview_location: scheduleForm.interview_location,
          interview_notes: scheduleForm.interview_notes,
          interview_type: identifiers.interviewType
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const applyUpdates = (applicationList, updater) => {
        const updated = applicationList.map((app) =>
          app._id === identifiers.applicationId
            ? updater(app)
            : app
        );
        return updated;
      };

      const updatedFields = {
        status: 'interview_scheduled',
        status_display: 'Scheduled',
        interview_date: scheduleForm.interview_date,
        interview_start_time: scheduleForm.interview_start_time,
        interview_end_time: scheduleForm.interview_end_time,
        interview_mode: scheduleForm.interview_mode,
        interview_link: scheduleForm.interview_link,
        interview_location: scheduleForm.interview_location,
        interview_notes: scheduleForm.interview_notes,
        last_updated: new Date().toISOString()
      };

      if (identifiers.interviewType === 'internship') {
        setInternshipApplications((prev) => {
          const updated = applyUpdates(prev, (app) => ({
            ...app,
            ...updatedFields
          }));
          updateCache(applications, updated);
          return updated;
        });
      } else {
        setApplications((prev) => {
          const updated = applyUpdates(prev, (app) => ({
            ...app,
            ...updatedFields
          }));
          updateCache(updated, internshipApplications);
          return updated;
        });
      }

      alert('Interview scheduled successfully!');
      window.dispatchEvent(new Event('interviewScheduled'));
      setScheduleSubmitting(false);
      closeScheduleModal();
      fetchApplications({ showSpinner: false });
    } catch (error) {
      console.error('Error scheduling interview:', error);
      const errorMessage = error.response?.data?.error || 'Failed to schedule interview. Please try again.';
      alert(errorMessage);
      setScheduleSubmitting(false);
    }
  };

  const handleViewCandidate = (application) => {
    if (!onViewCandidate) {
      return;
    }

    const candidateId = application.applicant_id || application.userId;
    const jobIdentifier =
      application.job_id ||
      application.jobId ||
      application.internship_id ||
      application.internshipId;
    const applicationType = (application.internship_id || application.internshipId)
      ? 'internship'
      : 'job';

    if (!candidateId || !jobIdentifier) {
      console.warn('Candidate tracking: missing candidate or job identifier', application);
      return;
    }

    onViewCandidate(candidateId, jobIdentifier, applicationType);
  };

  const toggleExpand = (applicationId) => {
    setExpandedApplication(expandedApplication === applicationId ? null : applicationId);
  };

  const updateApplicationStatus = async (application, newStatus) => {
    try {
      setUpdatingStatus(application._id);
      const token = localStorage.getItem('token');
      
      // Call the backend API to update the application status
      const response = await axios.put(
        buildApiUrl(`/api/recruiters/applications/${application._id}/status`),
        { status: newStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.status === 200) {
        const trackingRecord = response.data?.tracking_record;
        const updatedAtIso = new Date().toISOString();

        const mergeTrackingHistory = (history) => {
          const safeHistory = Array.isArray(history) ? history : [];
          if (!trackingRecord) {
            return safeHistory;
          }
          const filtered = safeHistory.filter(
            (record) => record && record._id !== trackingRecord._id
          );
          return [trackingRecord, ...filtered];
        };

        // Update the local state immediately for both jobs and internships
        if (viewType === 'jobs') {
          setApplications(prev => {
            const updated = prev.map(app => 
              app._id === application._id 
                ? { 
                    ...app, 
                    status: newStatus, 
                    status_display: newStatus.replace('_', ' ').charAt(0).toUpperCase() + newStatus.slice(1).replace('_', ' '),
                    last_updated: updatedAtIso,
                    tracking_history: mergeTrackingHistory(app.tracking_history)
                  }
                : app
            );
            updateCache(updated, internshipApplications);
            return updated;
          });
        } else {
          setInternshipApplications(prev => {
            const updated = prev.map(app => 
              app._id === application._id 
                ? { 
                    ...app, 
                    status: newStatus, 
                    status_display: newStatus.replace('_', ' ').charAt(0).toUpperCase() + newStatus.slice(1).replace('_', ' '),
                    last_updated: updatedAtIso,
                    tracking_history: mergeTrackingHistory(app.tracking_history)
                  }
                : app
            );
            updateCache(applications, updated);
            return updated;
          });
        }

        try {
          await fetchApplications({ showSpinner: false });
        } catch (refreshError) {
          console.warn('Unable to refresh applications after status update', refreshError);
        }

        alert(`✅ Status updated to ${newStatus.replace('_', ' ')} successfully! The candidate will be notified by email.`);
        
        // State and cached data refreshed above to reflect the latest tracking history.
      } else {
        throw new Error('Failed to update status');
      }
      
    } catch (err) {
      console.error('Error updating status:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update status. Please try again.';
      alert(errorMessage);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Get current applications based on view type
  const currentApplications = viewType === 'jobs' ? applications : internshipApplications;
  
  const filteredApplications = currentApplications.filter(app => {
    // Normalize user input so "Thika, Kenya" and "Thika Kenya" behave the same.
    // Replace punctuation with spaces, collapse whitespace, and lowercase.
    const normalizedSearch = (searchTerm || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
    const tokens = normalizedSearch.split(/\s+/).filter(Boolean);

    const searchableValues = [
      app.applicant_name,
      app.candidate_name,
      app.job_title,
      app.company_name,
      app.applicant_email,
      // Location fields from applicant profile (if present)
      app.applicant_city,
      app.applicant_country,
      app.applicant_location_display
    ]
      .filter(Boolean)
      .map((v) =>
        String(v)
          .toLowerCase()
          .replace(/[^\p{L}\p{N}]+/gu, ' ')
          .trim()
      );

    // Allow searching multiple cities/countries separated by spaces (e.g., "thika nairobi")
    // Match if ANY token matches ANY searchable field.
    const matchesSearch =
      tokens.length === 0 ||
      tokens.some((token) => searchableValues.some((val) => val.includes(token)));
    
    const matchesStatus =
      statusFilter === 'all' ||
      app.status === statusFilter ||
      (statusFilter === 'interview' && app.status === 'interview_scheduled');
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <ThemedLoadingSpinner 
        theme="recruiter"
        size="large"
        text="Loading candidate applications..."
        subText="Preparing your candidate tracker"
        showIcon={true}
        fullScreen={false}
      />
    );
  }

  if (error) {
    return (
      <div className="tracker-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>{error}</p>
        <button onClick={() => fetchApplications({ showSpinner: true })} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="recruiter-candidate-tracker">
      {/* Header */}
      {/* View Type Tabs */}
      <div className="view-type-tabs tracker-view-type-tabs" style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '10px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          className="tracker-view-tab"
          onClick={() => setViewType('jobs')}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: viewType === 'jobs' ? 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)' : 'transparent',
            color: viewType === 'jobs' ? 'white' : '#666',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className="fas fa-briefcase"></i>
          <span className="tracker-tab-text-full">Job Applications ({applications.length})</span>
          <span className="tracker-tab-text-short">Jobs ({applications.length})</span>
        </button>
        <button
          type="button"
          className="tracker-view-tab"
          onClick={() => setViewType('internships')}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: viewType === 'internships' ? 'linear-gradient(135deg, #20B2AA 0%, #17a2b8 100%)' : 'transparent',
            color: viewType === 'internships' ? 'white' : '#666',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className="fas fa-user-graduate"></i>
          <span className="tracker-tab-text-full">Internship Applications ({internshipApplications.length})</span>
          <span className="tracker-tab-text-short">Interns ({internshipApplications.length})</span>
        </button>
        <div className="tracker-view-tabs-spacer" style={{ flexGrow: 1 }} />
        <button 
          type="button"
          onClick={() => fetchApplications({ showSpinner: true })}
          className="btn btn-primary btn-post-job tracker-refresh-btn"
          aria-label="Refresh"
          title="Refresh"
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '500',
            borderRadius: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <i className="fas fa-sync-alt" aria-hidden="true"></i>
        </button>
      </div>

      {/* Stats Cards - New Design */}
      <div className="tracker-stats-grid">
        <div className="tracker-stat-card">
          <div className="tracker-stat-info">
            <p className="tracker-stat-title">Applications</p>
            <h3 className="tracker-stat-value">{stats.total}</h3>
            <div className="tracker-stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>0%</span>
            </div>
          </div>
          <div className="tracker-stat-icon-box tracker-stat-icon-blue">
            <i className="fas fa-users"></i>
          </div>
        </div>

        <div className="tracker-stat-card">
          <div className="tracker-stat-info">
            <p className="tracker-stat-title">Pending Review</p>
            <h3 className="tracker-stat-value">{stats.pending}</h3>
            <div className="tracker-stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>0%</span>
            </div>
          </div>
          <div className="tracker-stat-icon-box tracker-stat-icon-blue">
            <i className="fas fa-clock"></i>
          </div>
        </div>

        <div className="tracker-stat-card">
          <div className="tracker-stat-info">
            <p className="tracker-stat-title">Under Review</p>
            <h3 className="tracker-stat-value">{stats.reviewing}</h3>
            <div className="tracker-stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>0%</span>
            </div>
          </div>
          <div className="tracker-stat-icon-box tracker-stat-icon-blue">
            <i className="fas fa-eye"></i>
          </div>
        </div>

        <div className="tracker-stat-card">
          <div className="tracker-stat-info">
            <p className="tracker-stat-title">Interview Stage</p>
            <h3 className="tracker-stat-value">{stats.interview}</h3>
            <div className="tracker-stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>0%</span>
            </div>
          </div>
          <div className="tracker-stat-icon-box tracker-stat-icon-blue">
            <i className="fas fa-comments"></i>
          </div>
        </div>

        <div className="tracker-stat-card">
          <div className="tracker-stat-info">
            <p className="tracker-stat-title">Offers Extended</p>
            <h3 className="tracker-stat-value">{stats.offered}</h3>
            <div className="tracker-stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>0%</span>
            </div>
          </div>
          <div className="tracker-stat-icon-box tracker-stat-icon-blue">
            <i className="fas fa-handshake"></i>
          </div>
        </div>

        <div className="tracker-stat-card">
          <div className="tracker-stat-info">
            <p className="tracker-stat-title">Hired</p>
            <h3 className="tracker-stat-value">{stats.hired}</h3>
            <div className="tracker-stat-trend">
              <i className="fas fa-arrow-up"></i>
              <span>0%</span>
            </div>
          </div>
          <div className="tracker-stat-icon-box tracker-stat-icon-blue">
            <i className="fas fa-check-circle"></i>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="tracker-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, job title, email, city, or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <i className="fas fa-filter"></i>
          {shouldUseCustomDropdown ? (
            <EmployerFilterDropdown
              showCounts={false}
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS}
            />
          ) : (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          )}
        </div>
      </div>

      {/* Applications Grid/Table */}
      <div className="applications-grid-container">
        {filteredApplications.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
            <i className="fas fa-inbox" style={{ fontSize: '48px', color: '#FF8A65', marginBottom: '16px' }}></i>
            <h3 style={{ textAlign: 'center', margin: '0 auto 12px auto' }}>No applications found</h3>
            <p style={{ textAlign: 'center', margin: '0 auto', paddingLeft: '0', paddingRight: '0', width: '100%' }}>
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No candidates have applied to your job postings yet'}
            </p>
          </div>
        ) : (
          <div className="applications-table-wrapper">
            <table className="applications-table-new">
              <thead>
                <tr>
                  <th className="col-applicant">Applicant Name</th>
                  <th className="col-job-title">{viewType === 'jobs' ? 'Job Title' : 'Internship Title'}</th>
                  <th className="col-profile-pct">Profile %</th>
                  <th className="col-applied">Applied On</th>
                  <th className="col-status">Status</th>
                  <th className="col-view">View</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((application) => (
                  <React.Fragment key={application._id}>
                    <tr className="application-row">
                      <td className="col-applicant">
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          position: 'relative',
                          cursor: 'pointer'
                        }}>
                          <div style={{ 
                            position: 'relative', 
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: viewType === 'internships' && application.applicant_university ? '4px' : '0'
                          }}>
                            <span style={{ 
                              fontWeight: '500', 
                              color: '#333',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {application.applicant_name || application.candidate_name || 'Unknown Candidate'}
                            </span>
                            {(application.applicant_location_display || application.applicant_city || application.applicant_country) && (
                              <span style={{
                                fontSize: '12px',
                                color: '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%'
                              }}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '11px', color: '#FF8A65' }} />
                                {application.applicant_location_display ||
                                  [application.applicant_city, application.applicant_country].filter(Boolean).join(', ')}
                              </span>
                            )}
                            {viewType === 'internships' && application.applicant_university && (
                              <span style={{
                                fontSize: '12px',
                                color: '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%'
                              }}>
                                <FontAwesomeIcon icon={faGraduationCap} style={{ fontSize: '11px', color: '#FF8A65' }} />
                                {application.applicant_university}
                              </span>
                            )}
                            {/* Hover Tooltip */}
                            <div style={{
                              position: 'absolute',
                              top: 'calc(100% + 5px)',
                              left: '0',
                              background: 'white',
                              border: '2px solid #FF8A65',
                              borderRadius: '8px',
                              padding: '12px 16px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              zIndex: 9999,
                              minWidth: '280px',
                              opacity: 0,
                              visibility: 'hidden',
                              transition: 'opacity 0.2s ease, visibility 0.2s ease',
                              pointerEvents: 'none',
                              whiteSpace: 'normal'
                            }}
                            className="applicant-hover-tooltip"
                            >
                              <div style={{ marginBottom: '8px' }}>
                                <strong style={{ color: '#FF8A65', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                  📧 Email:
                                </strong>
                                <a 
                                  href={`mailto:${application.applicant_email}`}
                                  style={{ 
                                    color: '#3b82f6', 
                                    textDecoration: 'none',
                                    fontSize: '13px',
                                    wordBreak: 'break-all'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {application.applicant_email || 'No email'}
                                </a>
                              </div>
                              <div>
                                <strong style={{ color: '#FF8A65', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                  📱 Phone:
                                </strong>
                                <span style={{ color: '#333', fontSize: '13px' }}>
                                  {application.applicant_phone || application.phone || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="col-job-title">
                        <strong 
                          style={{ 
                            color: '#FF8A65',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%'
                          }}
                          title={application.job_title || 'N/A'}
                        >
                          {application.job_title || 'N/A'}
                        </strong>
                      </td>
                      <td className="col-profile-pct">
                        {(() => {
                          const pct = application.profile_completion_percentage;
                          const value = typeof pct === 'number' ? pct : (parseInt(pct, 10) || 0);
                          const clamped = Math.min(100, Math.max(0, value));
                          return (
                            <span style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }} title={`Profile completion: ${clamped}%`}>
                              {clamped}%
                            </span>
                          );
                        })()}
                      </td>
                      <td className="col-applied">
                        {formatDate(application.applied_at || application.created_at)}
                      </td>
                      <td className="col-status">
                        <span 
                          className="status-badge"
                          style={{ 
                            backgroundColor: getStatusColor(application.status),
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {(application.status_display || application.status || 'Pending').toLowerCase() === 'interview scheduled'
                            ? 'Scheduled'
                            : (application.status_display || application.status || 'Pending')}
                        </span>
                      </td>
                      <td className="col-view">
                        <div className="view-icons-container">
                          <span
                            onClick={() => handleViewCandidate(application)}
                            className="view-icon"
                            aria-label="View application"
                            title="View application"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </span>
                          <span
                            onClick={async () => {
                              try {
                                const candidateId = application.applicant_id || application.candidate_id || application.userId;
                                if (!candidateId) {
                                  alert('Unable to download CV: Candidate ID not found');
                                  return;
                                }

                                const token = localStorage.getItem('token');
                                if (!token) {
                                  alert('Please login to download CVs');
                                  return;
                                }

                                const loadingMsg = document.createElement('div');
                                loadingMsg.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #1f2937; color: white; padding: 1rem 2rem; border-radius: 8px; z-index: 99999; font-size: 14px;';
                                loadingMsg.textContent = '📄 Generating compressed CV...';
                                document.body.appendChild(loadingMsg);

                                const response = await fetch(buildApiUrl(`/api/recruiters/candidates/${candidateId}/download-cv`), {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });

                                document.body.removeChild(loadingMsg);

                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  
                                  const profile = {
                                    name: application.applicant_name || application.candidate_name,
                                    firstName: application.firstName || application.applicantFirstName,
                                    lastName: application.lastName || application.applicantLastName,
                                  };
                                  a.download = resolveDownloadFilename(response, profile, 'Candidate CV.zip', 'zip');
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                  
                                  const successMsg = document.createElement('div');
                                  successMsg.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #10b981; color: white; padding: 1rem 1.5rem; border-radius: 8px; z-index: 99999; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);';
                                  successMsg.textContent = '✅ CV downloaded successfully!';
                                  document.body.appendChild(successMsg);
                                  setTimeout(() => document.body.removeChild(successMsg), 3000);
                                } else {
                                  const errorData = await response.json().catch(() => ({ error: 'Failed to download CV' }));
                                  alert(errorData.error || 'Failed to download CV');
                                }
                              } catch (error) {
                                console.error('Error downloading CV:', error);
                                alert('An error occurred while downloading the CV. Please try again.');
                              }
                            }}
                            className="download-icon"
                            aria-label="Download CV"
                            title="Download Compressed CV with Akshar Jobs Branding"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </span>
                        </div>
                      </td>
                      <td className="col-actions">
                        <div className="actions-container">
                          <button
                            onClick={() => toggleExpand(application._id)}
                            className={`status-toggle-btn ${expandedApplication === application._id ? 'active' : ''}`}
                          >
                            <FontAwesomeIcon icon={expandedApplication === application._id ? faChevronUp : faChevronDown} />
                            <span>STATUS</span>
                          </button>
                          
                          <div className="action-menu-wrapper">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenActionMenu(openActionMenu === application._id ? null : application._id);
                              }}
                              style={{
                                background: openActionMenu === application._id ? '#FF8A65' : 'transparent',
                                border: '2px solid #FF8A65',
                                color: openActionMenu === application._id ? 'white' : '#FF8A65',
                                padding: '6px 10px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '36px',
                                transition: 'all 0.2s ease'
                              }}
                              title="Actions"
                            >
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </button>
                            
                            {openActionMenu === application._id && (
                              <div
                                className="action-menu-container"
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: 0,
                                  marginTop: '4px',
                                  background: 'white',
                                  border: '2px solid #FF8A65',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  zIndex: 1000,
                                  minWidth: '180px',
                                  maxWidth: 'calc(100vw - 40px)',
                                  overflow: 'hidden'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={async () => {
                                    try {
                                      const candidateId = application.applicant_id || application.candidate_id || application.userId;
                                      if (!candidateId) {
                                        alert('Unable to download CV: Candidate ID not found');
                                        return;
                                      }

                                      const token = localStorage.getItem('token');
                                      if (!token) {
                                        alert('Please login to download CVs');
                                        return;
                                      }

                                      // Show loading indicator
                                      const loadingMsg = document.createElement('div');
                                      loadingMsg.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #1f2937; color: white; padding: 1rem 2rem; border-radius: 8px; z-index: 99999;';
                                      loadingMsg.textContent = 'Generating CV...';
                                      document.body.appendChild(loadingMsg);

                                      const response = await fetch(buildApiUrl(`/api/recruiters/candidates/${candidateId}/download-cv`), {
                                        method: 'GET',
                                        headers: {
                                          'Authorization': `Bearer ${token}`
                                        }
                                      });

                                      document.body.removeChild(loadingMsg);

                                      if (response.ok) {
                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        
                                        const profile = {
                                          name: application.applicant_name || application.candidate_name,
                                          firstName: application.firstName || application.applicantFirstName,
                                          lastName: application.lastName || application.applicantLastName,
                                        };
                                        a.download = resolveDownloadFilename(response, profile, 'Candidate CV.zip', 'zip');
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                        setOpenActionMenu(null);
                                      } else {
                                        const errorData = await response.json().catch(() => ({ error: 'Failed to download CV' }));
                                        alert(errorData.error || 'Failed to download CV');
                                      }
                                    } catch (error) {
                                      console.error('Error downloading CV:', error);
                                      alert('An error occurred while downloading the CV. Please try again.');
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    fontSize: '14px',
                                    color: '#333',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  <FontAwesomeIcon icon={faFilePdf} style={{ color: '#dc2626', fontSize: '16px' }} />
                                  <span>Download Profile as PDF</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {expandedApplication === application._id && (
                      <tr className="expanded-row">
                        <td colSpan="7">
                          <div className="application-expanded">
                            {/* Quick Status Actions */}
                            <div className="expanded-section" style={{ marginBottom: '20px' }}>
                              <h4 style={{ marginBottom: '12px', color: '#333' }}>
                                <FontAwesomeIcon icon={faCheck} /> Quick Actions
                              </h4>
                              <div className="quick-actions-grid">
                                <button
                                  className="quick-action-btn reviewing"
                                  onClick={() => updateApplicationStatus(application, 'reviewing')}
                                  disabled={updatingStatus === application._id || application.status === 'reviewing'}
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  <span>Review</span>
                                </button>
                                <button
                                  className="quick-action-btn shortlisted"
                                  onClick={() => updateApplicationStatus(application, 'shortlisted')}
                                  disabled={updatingStatus === application._id || application.status === 'shortlisted'}
                                >
                                  <FontAwesomeIcon icon={faStar} />
                                  <span>Shortlist</span>
                                </button>
                                <button
                                  className="quick-action-btn interview"
                                  onClick={() => openScheduleModal(application)}
                                  disabled={updatingStatus === application._id}
                                >
                                  <FontAwesomeIcon icon={faComments} />
                                  <span>{['interview', 'interview_scheduled'].includes(application.status) ? 'Resched' : 'Schedule'}</span>
                                </button>
                                <button
                                  className="quick-action-btn offered"
                                  onClick={() => updateApplicationStatus(application, 'offered')}
                                  disabled={updatingStatus === application._id || application.status === 'offered'}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                  <span>Offer</span>
                                </button>
                                <button
                                  className="quick-action-btn hired"
                                  onClick={() => updateApplicationStatus(application, 'hired')}
                                  disabled={updatingStatus === application._id || application.status === 'hired'}
                                >
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                  <span>Hired</span>
                                </button>
                                <button
                                  className="quick-action-btn rejected"
                                  onClick={() => updateApplicationStatus(application, 'rejected')}
                                  disabled={updatingStatus === application._id || application.status === 'rejected'}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                  <span>Reject</span>
                                </button>
                              </div>
                              {updatingStatus === application._id && (
                                <div className="updating-status" style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                                  <i className="fas fa-spinner fa-spin"></i> Updating status and sending notification...
                                </div>
                              )}
                            </div>

                            {application.cover_letter && (
                              <div className="expanded-section tracker-cover-letter-section">
                                <h4 className="expanded-section-title">
                                  <FontAwesomeIcon icon={faComments} /> Cover Letter
                                </h4>
                                <div className="tracker-cover-letter-card">
                                  <p className="cover-letter">{application.cover_letter}</p>
                                </div>
                              </div>
                            )}

                            {application.tracking_history && application.tracking_history.length > 0 && (
                              <div className="expanded-section tracker-timeline-section">
                                <h4 className="expanded-section-title tracker-timeline-title">
                                  <FontAwesomeIcon icon={faClock} />
                                  Application Timeline
                                </h4>
                                <div className="timeline-modern">
                                  {application.tracking_history.map((record, index) => {
                                    // Get viewer's local timezone
                                    const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                    const viewerTimezoneLabel = formatTimezoneLabel(viewerTimezone);
                                    
                                    // Format time in viewer's local timezone
                                    const viewerTimeString = formatDateTimeInZone(
                                      record?.timestamp || record?.created_at || record?.employer_local_time || record?.candidate_local_time,
                                      viewerTimezone
                                    );
                                    
                                    // Also show employer and candidate times for reference (optional)
                                    const employerTimeString = formatDateTimeInZone(
                                      record?.employer_local_time || record?.timestamp || record?.created_at,
                                      record?.employer_timezone
                                    );
                                    const candidateTimeString = formatDateTimeInZone(
                                      record?.candidate_local_time || record?.timestamp || record?.created_at,
                                      record?.candidate_timezone
                                    );
                                    const employerTimezoneLabel = formatTimezoneLabel(record?.employer_timezone);
                                    const candidateTimezoneLabel = formatTimezoneLabel(record?.candidate_timezone);
                                    const timeDifferenceLabel = formatTimeDifference(record?.time_difference_minutes);
                                    
                                    return (
                                    <div
                                      key={index}
                                      className={`timeline-item-modern${index === 0 ? ' is-latest' : ''}`}
                                    >
                                      <div className="timeline-dot" aria-hidden="true" />
                                      {index < application.tracking_history.length - 1 && (
                                        <div className="timeline-line" aria-hidden="true" />
                                      )}
                                      <div className="timeline-card">
                                        <div className="timeline-card-header">
                                          <strong className="timeline-status">
                                              {record?.status ? record.status.replace('_', ' ') : 'Status Update'}
                                          </strong>
                                          <span className="timeline-badge">
                                              {index === 0 ? 'LATEST' : formatDate(record?.created_at || record?.timestamp)}
                                          </span>
                                        </div>
                                          <div className="timeline-times">
                                            <div className="timeline-time-row timeline-time-row-primary">
                                              <FontAwesomeIcon icon={faClock} className="timeline-time-icon" />
                                              <span className="timeline-time-text">
                                                <strong>Your Time</strong>
                                                {` (${viewerTimezoneLabel}) · ${viewerTimeString}`}
                                              </span>
                                            </div>
                                            {(employerTimezoneLabel !== viewerTimezoneLabel || candidateTimezoneLabel !== viewerTimezoneLabel) && (
                                              <>
                                                {employerTimezoneLabel !== viewerTimezoneLabel && (
                                                  <div className="timeline-time-row timeline-time-row-secondary">
                                                    <FontAwesomeIcon icon={faClock} className="timeline-time-icon" />
                                                    <span className="timeline-time-text">
                                                      <strong>Employer:</strong> {employerTimeString} ({employerTimezoneLabel})
                                                    </span>
                                                  </div>
                                                )}
                                                {candidateTimezoneLabel !== viewerTimezoneLabel && (
                                                  <div className="timeline-time-row timeline-time-row-secondary">
                                                    <FontAwesomeIcon icon={faClock} className="timeline-time-icon" />
                                                    <span className="timeline-time-text">
                                                      <strong>Jobseeker:</strong> {candidateTimeString} ({candidateTimezoneLabel})
                                                    </span>
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                          {record?.notes && (
                                          <p className="timeline-note">
                                            💬 {record.notes}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {scheduleModalOpen && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div
            className="modal-content"
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              width: '96%',
              maxWidth: '620px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh'
            }}
          >
            <div
              className="modal-header"
              style={{
                padding: '24px 28px',
                background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                borderBottom: '2px solid rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: '#ffffff', fontSize: '22px', fontWeight: 700 }}>
                  <FontAwesomeIcon icon={faComments} style={{ color: '#ffffff', marginRight: '10px' }} />
                  {['interview', 'interview_scheduled'].includes(scheduleModalApplication?.status) ? 'Reschedule Interview' : 'Schedule Interview'}
                </h2>
                {scheduleModalApplication && (
                  <p style={{ margin: '8px 0 0 0', color: '#ffffff', fontSize: '14px', opacity: 0.95 }}>
                    {scheduleModalApplication.applicant_name || scheduleModalApplication.candidate_name || 'Candidate'} ·{' '}
                    {scheduleModalApplication.job_title || scheduleModalApplication.internship_title || 'Role'}
                  </p>
                )}
              </div>
              <button
                onClick={closeScheduleModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#ffffff'
                }}
                aria-label="Close schedule interview modal"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleScheduleSubmit} style={{ padding: '24px 28px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                    Interview Date *
                  </label>
                  <input
                    type="date"
                    value={scheduleForm.interview_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, interview_date: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                    Start Time *
                  </label>
                  <input
                    type="time"
                    value={scheduleForm.interview_start_time}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, interview_start_time: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                    End Time
                  </label>
                  <input
                    type="time"
                    value={scheduleForm.interview_end_time}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, interview_end_time: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                    Interview Mode *
                  </label>
                  {shouldUseCustomDropdown ? (
                    <EmployerFilterDropdown
                      showCounts={false}
                      value={scheduleForm.interview_mode}
                      onChange={(nextValue) => setScheduleForm((prev) => ({
                        ...prev,
                        interview_mode: nextValue,
                        interview_link: nextValue === 'online' ? prev.interview_link : '',
                        interview_location: nextValue === 'in-person' ? prev.interview_location : ''
                      }))}
                      options={INTERVIEW_MODE_OPTIONS}
                    />
                  ) : (
                  <select
                    value={scheduleForm.interview_mode}
                    onChange={(e) => setScheduleForm((prev) => ({
                      ...prev,
                      interview_mode: e.target.value,
                      interview_link: e.target.value === 'online' ? prev.interview_link : '',
                      interview_location: e.target.value === 'in-person' ? prev.interview_location : ''
                    }))}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  >
                    {INTERVIEW_MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  )}
                </div>
              </div>

              {scheduleForm.interview_mode === 'online' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                    Meeting Link *
                  </label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={scheduleForm.interview_link}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, interview_link: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}

              {scheduleForm.interview_mode === 'in-person' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="Office address"
                    value={scheduleForm.interview_location}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, interview_location: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                  Notes
                </label>
                <textarea
                  placeholder="Share any interview instructions or agenda..."
                  rows="3"
                  value={scheduleForm.interview_notes}
                  onChange={(e) => setScheduleForm((prev) => ({ ...prev, interview_notes: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div
                className="modal-footer"
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px'
                }}
              >
                <button
                  type="button"
                  onClick={closeScheduleModal}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    background: '#ffffff',
                    color: '#6b7280',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  disabled={scheduleSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                    color: '#ffffff',
                    fontWeight: 600,
                    cursor: scheduleSubmitting ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(255, 112, 67, 0.35)'
                  }}
                  disabled={scheduleSubmitting}
                >
                  {scheduleSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} /> Save Interview
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruiterCandidateTracker;

